import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Loader2, Calendar, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const ProcessingReport = () => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchReport();
  }, [selectedDate]);

  const fetchReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/processing/report/day-wise?date=${selectedDate}`);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!reportData?.transactions?.length) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Transaction ID', 'Bundle Barcode', 'Design', 'Type', 'Timestamp', 'Staff', 'Transfer ID'];
    const rows = reportData.transactions.map(t => [
      t.txn_id,
      t.bundle_barcode,
      t.design_name || '-',
      t.txn_type,
      t.timestamp,
      t.staff_name,
      t.transfer_id || '-'
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `processing_report_${selectedDate}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/sections/processing')}
              className="text-white hover:bg-[#2D5F4F]"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Day Wise Transaction Report</h1>
              <p className="text-sm text-gray-400">Processing Section</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Date Filter */}
        <Card className="bg-white mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-500" />
                <Label>Select Date</Label>
              </div>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-48"
              />
              <Button variant="outline" onClick={exportToCSV} disabled={!reportData?.transactions?.length}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {reportData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-white">
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Inward Bundles</p>
                <p className="text-3xl font-bold text-green-600">{reportData.inward_bundles}</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Outward Bundles</p>
                <p className="text-3xl font-bold text-blue-600">{reportData.outward_bundles}</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Total Transactions</p>
                <p className="text-3xl font-bold text-gray-800">{reportData.transactions?.length || 0}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Transactions Table */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg">Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
              </div>
            ) : reportData?.transactions?.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Bundle</TableHead>
                      <TableHead>Design</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Staff</TableHead>
                      <TableHead>Related Section</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.transactions.map((txn) => (
                      <TableRow key={txn.txn_id}>
                        <TableCell className="text-sm">
                          {new Date(txn.timestamp).toLocaleTimeString()}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{txn.bundle_barcode}</TableCell>
                        <TableCell>{txn.design_name || '-'}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            txn.txn_type === 'inward' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {txn.txn_type}
                          </span>
                        </TableCell>
                        <TableCell>{txn.staff_name}</TableCell>
                        <TableCell>{txn.related_section || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No transactions for {selectedDate}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProcessingReport;
